// uiEvents — lightweight, fire-and-forget product telemetry for the staff portal.
//
// Records how people move through the CRM (page views, nav/card/button clicks,
// searches, quick actions, time-to-first-action) so the Home/nav redesign can
// be driven by real usage instead of guesswork.
//
// Design goals:
//  - NEVER block or break the UI. Every path is try/caught and swallowed.
//  - Cheap: events are queued in memory and flushed in small batches (every 4s,
//    on tab-hide, and on pagehide) rather than one network call per click.
//  - Drop-in: mount `useUiTracking(member)` once in Layout for page-view +
//    time-to-action tracking. Call `logUiEvent(...)` (or the helpers) from any
//    click handler you want to instrument — incrementally, no big refactor.
//
// Backed by the public.ui_events table (see 20260616_ui_events_telemetry.sql).

import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "./supabase";

type EventType =
  | "page_view"
  | "nav_click"
  | "card_click"
  | "button_click"
  | "quick_action"
  | "search"
  | "search_select"
  | "ask_greg"
  | "time_to_action"
  | "back_nav";

interface UiEventInput {
  target?: string | null;
  path?: string | null;
  detail?: Record<string, unknown>;
}

interface QueuedEvent {
  user_id: string | null;
  role: string | null;
  session_id: string;
  event_type: EventType;
  path: string | null;
  target: string | null;
  detail: Record<string, unknown>;
  client_ts: string;
}

// ---- module state -----------------------------------------------------------

let actorId: string | null = null;
let actorRole: string | null = null;
let queue: QueuedEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

const SESSION_KEY = "bp_ui_session";

function sessionId(): string {
  try {
    let s = sessionStorage.getItem(SESSION_KEY);
    if (!s) {
      s = (crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`);
      sessionStorage.setItem(SESSION_KEY, s);
    }
    return s;
  } catch {
    return "no-session";
  }
}

/** Tell the logger who the current user is. Call once auth resolves. */
export function setUiActor(member: { id?: string | null; role?: string | null } | null) {
  actorId = member?.id ?? null;
  actorRole = member?.role ?? null;
}

// ---- core -------------------------------------------------------------------

export function logUiEvent(type: EventType, input: UiEventInput = {}) {
  try {
    queue.push({
      user_id: actorId,
      role: actorRole,
      session_id: sessionId(),
      event_type: type,
      path: input.path ?? (typeof window !== "undefined" ? window.location.pathname + window.location.search : null),
      target: input.target ?? null,
      detail: input.detail ?? {},
      client_ts: new Date().toISOString(),
    });
    scheduleFlush();
  } catch {
    /* never throw from telemetry */
  }
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flush();
  }, 4000);
}

async function flush() {
  if (queue.length === 0) return;
  const batch = queue;
  queue = [];
  try {
    // occurred_at defaults to now() server-side; we keep client_ts for skew checks.
    await supabase.from("ui_events").insert(batch);
  } catch {
    // On failure, don't retry aggressively — drop to avoid unbounded growth.
    // Telemetry is best-effort; losing a few events is fine.
  }
}

// Flush on tab-hide / navigation away so we don't lose the tail of a session.
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") void flush();
  });
  window.addEventListener("pagehide", () => void flush());
}

// ---- convenience helpers ----------------------------------------------------

export const logNavClick = (key: string) => logUiEvent("nav_click", { target: key });
export const logCardClick = (key: string) => logUiEvent("card_click", { target: key });
export const logButtonClick = (label: string, detail?: Record<string, unknown>) =>
  logUiEvent("button_click", { target: label, detail });
export const logQuickAction = (key: string) => logUiEvent("quick_action", { target: key });
export const logSearch = (query: string, resultCount?: number) =>
  logUiEvent("search", { detail: { query, result_count: resultCount } });
export const logSearchSelect = (query: string, picked: string) =>
  logUiEvent("search_select", { target: picked, detail: { query } });

// ---- route + time-to-action tracking (mount once in Layout) -----------------

/**
 * Mount once near the top of the authenticated app (Layout). Logs a `page_view`
 * on every route change with time-on-previous-page, and a one-shot
 * `time_to_action` measuring how long after a page loads the user first acts
 * (click / keydown). That gap is the clearest single signal of friction.
 */
export function useUiTracking(member: { id?: string | null; role?: string | null } | null) {
  const location = useLocation();
  const enteredAt = useRef<number>(Date.now());
  const prevPath = useRef<string | null>(null);
  const acted = useRef<boolean>(false);

  // keep actor fresh
  useEffect(() => {
    setUiActor(member);
  }, [member]);

  useEffect(() => {
    const path = location.pathname + location.search;
    const now = Date.now();

    // page_view for the new path, with dwell time on the previous one
    logUiEvent("page_view", {
      path,
      detail: prevPath.current
        ? { from_path: prevPath.current, prev_dwell_ms: now - enteredAt.current }
        : {},
    });

    prevPath.current = path;
    enteredAt.current = now;
    acted.current = false;

    // first-action timer for this page
    const onFirstAction = () => {
      if (acted.current) return;
      acted.current = true;
      logUiEvent("time_to_action", { path, detail: { ms: Date.now() - now } });
      cleanup();
    };
    const cleanup = () => {
      document.removeEventListener("pointerdown", onFirstAction, true);
      document.removeEventListener("keydown", onFirstAction, true);
    };
    document.addEventListener("pointerdown", onFirstAction, true);
    document.addEventListener("keydown", onFirstAction, true);

    return cleanup;
  }, [location.pathname, location.search]);
}
